import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Footer } from "risa-oasis-ui_v2";
import { FirestoreService } from "../../api/firebase/firestoreService";
import { submitFaxFormQuestionaire } from "../../api/postCall/submitFaxFormQuestionaire";
import { LoaderMessage } from "../../components/loaderMessage/loaderMessage";
import { MedicalPaOrderStatus } from "../../enums/medicalPaOrderStatus";
import { getAllQuestionsForOrder } from "../../redux/slice/formQuestions-slice";
import { getMedicalPaOrderDetailsFor } from "../../redux/slice/medicalPa/singleMedicalPaOrder";
import { AppDispatch, RootState } from "../../redux/store/store";
import { logError } from "../../utils/customLogger";
import { generateFullName } from "../../utils/generateOrderDataForTable";
import FormQuestions from "./components/formQuestions/formQuestions";
import { QuestionnaireState } from "./types";

const ProcedureQuestionaireLayout = () => {
  const { singleMedicalPaOrder, isLoading } = useSelector(
    (state: RootState) => state.singleMedicalPaOrder,
  );

  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );

  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const [faxDataToSend, setFaxDataToSend] = useState<QuestionnaireState | null>(
    null,
  );
  const [isFaxDataLoading, setIsFaxDataLoading] = useState<boolean>(false);
  const [questionnaireStatus, setQuestionnaireStatus] = useState<string | null>(
    null,
  );
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [currentAnswers, setCurrentAnswers] = useState<
    { id: string; answer: any }[]
  >([]);
  const [faxPdfIntervalId, setFaxPdfIntervalId] =
    useState<NodeJS.Timeout | null>(null);
  const { data: singleMedicalPaFormConfig } = useSelector(
    (state: RootState) => state.singleMedicalPaFormConfig,
  );

  const { id } = useParams();
  const [unsubscribe, setUnsubscribe] = useState<(() => void) | null>(null);

  const navigateBack = (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    try {
      navigate(`/worklists/medical-pa-form/${id}`);
    } catch (error) {
      logError(error as Error);
    }
  };

  const getFaxPdfLinkRecursively = () => {
    // Clear any existing interval
    if (faxPdfIntervalId) {
      clearInterval(faxPdfIntervalId);
    }

    const intervalId = setInterval(async () => {
      try {
        const submissionInfoDoc = await FirestoreService.getDocument(
          `medical_pa_orders/${id}/submission_info/output`,
        );
        const faxPdfLink = (submissionInfoDoc as any)?.filled_pdf_link;
        if (faxPdfLink) {
          clearInterval(intervalId);
          setFaxPdfIntervalId(null);
          navigate(`/worklists/final-document/${id}`, {
            state: { faxPdfLink },
          });
          setIsSubmitting(false);
        }
      } catch (error) {
        logError(error as Error, "Error fetching fax PDF link");
        clearInterval(intervalId);
        setFaxPdfIntervalId(null);
      }
    }, 500);

    // Store the interval ID in state
    setFaxPdfIntervalId(intervalId);
  };

  useEffect(() => {
    if (id) {
      dispatch(getMedicalPaOrderDetailsFor(id));
      dispatch(getAllQuestionsForOrder(id));
    }
  }, [id, dispatch]);

  // Cleanup interval on component unmount
  useEffect(() => {
    return () => {
      if (faxPdfIntervalId) {
        clearInterval(faxPdfIntervalId);
      }
    };
  }, [faxPdfIntervalId]);

  const getFormQuestions = async (orderId: string) => {
    try {
      setIsFaxDataLoading(true);
      const unsubscribeFunction = await FirestoreService.listenToDocument<any>(
        `/medical_pa_orders/${orderId}/questionnaire/`,
        "fax_questionnaire",
        (document) => {
          setFaxDataToSend(document);
          setQuestionnaireStatus(document?.status ?? null);
        },
      );
      setUnsubscribe(() => unsubscribeFunction);
    } catch (error) {
      logError(error as Error, "Error fetching form questions");
      setFaxDataToSend(null);
      setQuestionnaireStatus("error");
    } finally {
      setIsFaxDataLoading(false);
    }
  };

  const handleAnswersChange = (answers: { id: string; answer: any }[]) => {
    setCurrentAnswers(answers);
  };

  const updateFaxQuestionnaireDocument = async (updatedData: any) => {
    try {
      // Merge the original data with the new answers
      const updatedQuestionTree = updateQuestionTreeWithAnswers(
        updatedData.question_tree || [],
        currentAnswers,
      );

      // Prepare the update data with new fields
      const updateData = {
        ...updatedData,
        question_tree: updatedQuestionTree,
        updated_at: new Date().toISOString(),
        updated_by: user?.email || "unknown",
      };

      //Update the fax questionnaire document
      await FirestoreService.updateDocument(
        `medical_pa_orders/${id}/questionnaire`,
        "fax_questionnaire",
        updateData,
      );
    } catch (error) {
      logError(error as Error, "Error updating fax questionnaire document");
    }
  };

  // Helper function to update question tree with new answers
  const updateQuestionTreeWithAnswers = (
    questionTree: any[],
    answers: { id: string; answer: any }[],
  ): any[] => {
    return questionTree.map((question) => {
      const answer = answers.find((a) => a.id === question.id);
      let updatedQuestion = { ...question };

      if (answer) {
        const answerValue = answer.answer;

        // Handle multi-correct questions (array of selected option IDs)
        if (
          question.question_type === "multi_correct" &&
          Array.isArray(answerValue)
        ) {
          // Map each selected option ID to its corresponding option object
          const selectedOptions = answerValue.map((optionId) => {
            const selectedOption = question.options?.find(
              (option: any) => option.id === optionId,
            );
            return selectedOption
              ? { id: selectedOption.id, text: selectedOption.text }
              : { id: optionId, text: optionId };
          });

          updatedQuestion.answer = selectedOptions;
        }
        // Handle single-correct and other question types
        else {
          const selectedOption = question.options?.find(
            (option: any) => option.id === answerValue,
          );

          if (selectedOption) {
            updatedQuestion.answer = [
              {
                id: selectedOption.id,
                text: selectedOption.text,
              },
            ];
          } else {
            updatedQuestion.answer = [
              {
                id: answerValue,
                text: answerValue,
              },
            ];
          }
        }
      }

      // Recursively update children if they exist
      if (question.children && Object.keys(question.children).length > 0) {
        const updatedChildren: any = {};
        for (const [key, childQuestions] of Object.entries(question.children)) {
          updatedChildren[key] = updateQuestionTreeWithAnswers(
            childQuestions as any[],
            answers,
          );
        }
        updatedQuestion.children = updatedChildren;
      }

      return updatedQuestion;
    });
  };

  useEffect(() => {
    if (id) {
      getFormQuestions(id);
      return () => {
        if (unsubscribe) {
          unsubscribe();
        }
      };
    }
  }, [id]);

  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      if (faxDataToSend) {
        await updateFaxQuestionnaireDocument(faxDataToSend);
      }
      const formId = singleMedicalPaFormConfig?.id;
      if (formId) {
        await submitFaxFormQuestionaire({
          form_id: formId,
          order_id: singleMedicalPaOrder?.id ?? "",
        });
        getFaxPdfLinkRecursively();
      } else {
        navigate(`/worklists/final-document/${id}`);
      }
    } catch (error) {
      logError(error as Error, "Error saving fax form data and submitting");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {isLoading ? (
        <>
          <LoaderMessage message="Fetching form questions..." />
        </>
      ) : (
        <div className="procedure-pa-form__container flex h-full flex-col bg-[white]">
          <div className="procedure-pa-form__body flex flex-1 flex-col overflow-hidden rounded px-4 py-2">
            <div className="procedure-pa-form__body--header mb-4">
              <div className="procedure-pa-form__body--header-details flex items-center gap-2">
                <div className="procedure-pa-form__body patient-name text-h16 font-semibold leading-6 text-primaryGray-1">
                  {generateFullName(
                    singleMedicalPaOrder?.demographics?.first_name ?? "",
                    "",
                    singleMedicalPaOrder?.demographics?.last_name ?? "",
                  )}
                </div>
                <div className="procedure-pa-form__body page-name text-sm font-semiBold text-black">
                  {" "}
                  ({singleMedicalPaOrder?.demographics?.patient_mrn ?? ""})
                </div>
                {/* Removing this as it has no implementation yet*/}
                {/* <div className="procedure-pa-form__attachements ml-auto">
                  <Button
                    disabled={false}
                    children={
                      singleMedicalPaOrder?.status?.financial_review ===
                        MedicalPaOrderStatus.DENIED ||
                      singleMedicalPaOrder?.status?.financial_review ===
                        MedicalPaOrderStatus.PENDING ||
                      singleMedicalPaOrder?.status?.financial_review ===
                        MedicalPaOrderStatus.APPROVED
                        ? "View Letter"
                        : "Manage Attachments"
                    }
                    onClick={handleViewLetter}
                    buttonType={"secondary"}
                    size={"medium"}
                  />
                </div> */}
              </div>
            </div>
            <div className="procedure-pa-form__body content flex flex-1 overflow-hidden">
              <FormQuestions
                enableSubmitButton={() => {}}
                makePostCallForFormSubmission={false}
                docId={id ?? ""}
                isLoading={(value) => {}}
                faxData={faxDataToSend}
                questionnaireStatus={questionnaireStatus}
                isQuestionnaireLoading={isFaxDataLoading}
                onAnswersChange={handleAnswersChange}
              />
            </div>
          </div>

          <div className="procedure-pa-form__footer">
            <Footer
              cancelButtonText="Go Back"
              submitButtonText={
                isSubmitting
                  ? "Reviewing..."
                  : singleMedicalPaOrder?.status?.financial_review ===
                        MedicalPaOrderStatus.PENDING ||
                      singleMedicalPaOrder?.status?.financial_review ===
                        MedicalPaOrderStatus.DENIED ||
                      singleMedicalPaOrder?.status?.financial_review ===
                        MedicalPaOrderStatus.APPROVED
                    ? "Go Back to Orders"
                    : "Review"
              }
              onCancelClick={() => navigateBack(null)}
              onSubmitClick={handleSubmit}
              isDisabled={
                questionnaireStatus === "in_progress" || isSubmitting
                  ? true
                  : false
              }
            />
          </div>
        </div>
      )}
    </>
  );
};

export default ProcedureQuestionaireLayout;
